/**
 * Traditional Chinese translations
 */
import type { TranslationKeys } from '../utils/i18n';

export const zhHant: TranslationKeys = {
  controls: {
    maxTime: '最大時間',
    maxTimeMinutes: '最大時間（分鐘）',
    travelMode: '出行方式',
    speed: '速度',
    mapStyle: '地圖樣式',
    language: '語言',
    share: '分享',
    play: '播放',
    pause: '暫停',
    restart: '重新開始',
    loading: '載入中...',
    showControls: '顯示控制',
    hideControls: '隱藏控制',
    expandControls: '展開控制',
    collapseControls: '摺疊控制',
    selectLocationHint: '請在地圖上選擇一個位置開始',
  },
  travelModes: {
    driving: '駕車',
    walking: '步行',
    cycling: '騎行',
    transit: '公共交通',
  },
  speed: {
    verySlow: '0.1x（非常慢）',
    slow: '0.25x（慢）',
    half: '0.5x（一半）',
    normal: '1x（正常）',
    fast: '1.5x（快）',
    veryFast: '2x（非常快）',
  },
  mapStyles: {
    streets: '街道',
    satellite: '衛星',
  },
  languages: {
    en: 'English',
    'zh-Hans': '简体中文 (Simplified Chinese)',
    'zh-Hant': '繁體中文 (Traditional Chinese)',
    es: 'Español (Spanish)',
    fr: 'Français (French)',
    de: 'Deutsch (German)',
    ja: '日本語 (Japanese)',
    ko: '한국어 (Korean)',
  },
  addressInput: {
    label: '搜尋地址',
    placeholder: '輸入地址以計算時間地圖',
    noResults: '未找到結果',
    loading: '載入建議中',
  },
  error: {
    retry: '重試',
    dismiss: '關閉錯誤',
    somethingWentWrong: '出了點問題。請重新整理頁面。',
    browserNotSupported: '您的瀏覽器不支援此應用程式。請使用現代瀏覽器。',
    usageLimitReached: '您已達到測試版限制的 5 次計算。感謝您的試用！如果您對此專案感興趣，請私訊我。',
  },
  progress: {
    minutes: '分鐘',
  },
  share: {
    urlCopied: 'URL已複製到剪貼簿！',
    shareUrl: '分享URL',
  },
  emailGate: {
    title: '登入時間地圖測試版',
    subtitle: '輸入您的電子郵件地址以接收登入連結',
    emailPlaceholder: 'your.email@example.com',
    emailLabel: '電子郵件地址',
    sendButton: '發送登入連結',
    sendingButton: '正在發送登入連結...',
    checkEmailTitle: '檢查您的郵箱',
    checkEmailMessage: '我們已向 {email} 發送了登入連結',
    checkEmailInstruction: '點擊電子郵件中的連結以繼續使用時間地圖。',
    checkEmailHelp: '沒有收到郵件？請檢查您的垃圾郵件資料夾，或使用其他電子郵件地址重試。',
    emailRequired: '請輸入您的電子郵件地址',
    emailInvalid: '請輸入有效的電子郵件地址',
    domainRestriction: '測試版訪問僅允許 @gmail.com、@outlook.com 和 @qq.com 電子郵件地址。',
    domainNotAllowed: '測試版訪問目前僅限於 Gmail、Outlook 和 QQ 電子郵件地址',
    typoSuggestion: '您是否想輸入 @{domain}？',
    rateLimitExceeded: '請求過多。請稍等片刻後重試。',
    invalidEmailFormat: '電子郵件地址無效。請檢查後重試。',
    failedToSend: '發送登入連結失敗。請重試。',
    magicLinkFailed: '發送魔法連結失敗：{error}',
    testMeButton: '測試',
    bannerText: '時間地圖',
    betaTag: '測試版',
  },
  testCode: {
    title: '輸入測試碼',
    subtitle: '有測試碼嗎？在下面輸入以開始',
    placeholder: '輸入代碼',
    button: '使用代碼繼續',
    haveCodeLink: '有測試碼嗎？',
    useEmailLink: '改用電子郵件',
    invalid: '測試碼無效',
    expired: '此測試碼已過期',
    limitReached: '此測試碼已達到使用限制',
    optionalEmailTitle: '保存您的進度（可選）',
    optionalEmailSubtitle: '想保存您的工作嗎？在下面輸入您的電子郵件（或暫時跳過）',
    optionalEmailPlaceholder: 'your.email@example.com（可選）',
    skipButton: '暫時跳過',
    submitEmailButton: '繼續',
  },
  usageIndicator: {
    template: '剩餘{count}次計算',
    loading: '載入中...',
  },
  disclaimer: {
    loading: '載入中...',
    title: '重要聲明',
    content: '本專案為測試版/實驗性專案。所有地圖資料均由第三方服務 Mapbox 提供，未經獨立驗證。地圖可能包含不準確資訊，不應用於關鍵決策。旅行時間計算僅為估算值，準確性未經驗證。對於因使用本應用程式而產生的任何不準確、錯誤或後果，我們不承擔任何責任。',
    personalStance: '地圖邊界和內容不代表個人立場。本應用使用的地圖可能包含不準確資訊，請注意甄別。',
    consent: '繼續使用本應用程式即表示您已知曉並接受上述所有條款和免責聲明。',
  },
  intro: {
    title: '探索您的可達範圍',
    message: '從任意地點出發，在指定時間內能到達多遠。\n\n透過地圖動畫直觀展示出行時間範圍，幫助您在選擇居住地、工作地點或出行目的地時做出更明智的決策。',
    step1: '在地圖上搜尋或點擊設定出發點',
    step2: '地圖會用動畫顯示您的出行時間可達範圍',
    step3: '設定出行時間和交通方式',
    step4: '點擊播放，查看可達範圍如何隨時間擴展',
    step5: '產生分享連結，把這個視覺化結果分享給其他人',
    next: '下一步',
    skip: '跳過',
    gotIt: '知道了',
    startTour: '開始引導',
  },
};

